import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye, Webhook } from 'lucide-react';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { DataTable } from '../../../../components/ui/DataTable';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { fetchAdminOptions } from '../../../../features/admins/adminsThunks';
import {
  fetchWebhookHistory,
  fetchWebhookHistorySellers,
} from '../../../../features/webhookHistory/webhookHistoryThunks';
import {
  resetFilters,
  setDateRange,
  setFilter,
  setPage,
  setRowsPerPage,
} from '../../../../features/webhookHistory/webhookHistorySlice';
import {
  getDispatchStateLabel,
  getDispatchStateTone,
} from '../../../../features/webhookHistory/webhookHistoryConstants';
import { getStatusLabel, getSubstatusLabel } from '../../../../features/webhooks/webhooksUtils';
import { WebhookHistoryFilters } from './components/WebhookHistoryFilters';
import { WebhookPayloadModal } from './components/WebhookPayloadModal';

const formatDateTime = (value) => (value ? new Date(value).toLocaleString('es-CL') : '-');

/**
 * Every webhook the platform tried to send, for the internal team. One row per
 * shipment and status pair, as `DeliveryWebhook` keeps them: a retry updates
 * the row rather than adding one, so the "Envío" column is the latest outcome.
 */
export const WebhookHistory = () => {
  const dispatch = useDispatch();
  const { rows, totalCount, loading, error, filters, sellers } = useSelector(
    (state) => state.webhookHistory
  );
  const adminOptions = useSelector((state) => state.admins.options);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    dispatch(fetchAdminOptions());
    dispatch(fetchWebhookHistorySellers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchWebhookHistory());
  }, [dispatch, filters]);

  const handleFilterChange = useCallback(
    (key, value) => dispatch(setFilter({ key, value })),
    [dispatch]
  );

  const columns = [
    {
      header: 'FECHA',
      cell: (row) => <span className="whitespace-nowrap">{formatDateTime(row.created_at)}</span>,
    },
    {
      header: 'OPERADOR',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-ninja-dark dark:text-white">{row.admin?.name || row.admin?.email || '—'}</span>
          {row.admin?.name && <span className="text-xs text-text-muted">{row.admin.email}</span>}
        </div>
      ),
    },
    {
      header: 'SELLER',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-ninja-dark dark:text-white">{row.seller?.social_reason || '—'}</span>
          <span className="text-xs text-text-muted">{row.seller?.email || ''}</span>
        </div>
      ),
    },
    {
      header: 'ORDEN',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.order ? `#${row.order.id}` : '—'}</span>
          {row.order?.reference_id && (
            <span className="text-xs text-text-muted">{row.order.reference_id}</span>
          )}
        </div>
      ),
    },
    {
      header: 'ESTADO',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold">{getStatusLabel(row.status)}</span>
          {row.substatus && row.substatus !== 'not_specified' && (
            <span className="text-xs text-text-muted">
              {getSubstatusLabel(row.status, row.substatus)}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'ENVÍO',
      cell: (row) => (
        <ToneBadge tone={getDispatchStateTone(row.dispatch_state)} testId="webhook-dispatch-state">
          {getDispatchStateLabel(row.dispatch_state)}
        </ToneBadge>
      ),
    },
    {
      header: 'URL DESTINO',
      cell: (row) => (
        <span className="block max-w-[260px] truncate text-text-secondary" title={row.url}>
          {row.url}
        </span>
      ),
    },
    {
      header: 'ACCIONES',
      cell: (row) => (
        <div className="flex justify-end">
          <Button
            variant="subtle"
            size="sm"
            iconOnly
            icon={Eye}
            title="Ver payload"
            aria-label={`Ver payload del webhook ${row.id}`}
            onClick={() => setSelected(row)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <PageHeader
        icon={Webhook}
        iconClassName="bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400"
        title="Historial de Webhooks"
        description="Cada webhook que la plataforma intentó enviar a los sistemas de los sellers, con el resultado del envío."
      />

      <FilterBar variant="bare">
        <WebhookHistoryFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onDateRangeChange={(range) => dispatch(setDateRange(range))}
          onReset={() => dispatch(resetFilters())}
          adminOptions={adminOptions}
          sellerOptions={sellers}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={loading}
        error={error}
        onRetry={() => dispatch(fetchWebhookHistory())}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        itemLabel="webhooks"
        onPageChange={(page) => dispatch(setPage(page))}
        onRowsPerPageChange={(rowsPerPage) => dispatch(setRowsPerPage(rowsPerPage))}
        emptyMessage="No hay envíos de webhooks para estos filtros."
      />

      <WebhookPayloadModal webhook={selected} onClose={() => setSelected(null)} />
    </div>
  );
};
